import { useMemo, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { AppDialog } from '../../components/AppDialog';
import { CellContextMenu, canBulkCopy } from '../../components/gridEdit';
import { useNotify } from '../../components/notifications';
import { fetchStockStdOptions } from './api';
import { LEVEL_COLS, LEVEL_EDIT } from './componentColumns';
import { NA_OPTION, type ReagentLookupOption, type ReagentPrepComponent, type ReagentPrepDetail } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// The Calibration levels grid — XAF ReagentPreparation_ReagentPrepLogs_ListView_
// Calibration, AllowEdit="True" InlineEditMode="Batch" (Model.xafml :38674).
//
// Edits are BUFFERED by the detail page and stored with the header's Save, as
// in XAF (the nested list shares the detail view's ObjectSpace). This component
// only edits the buffer; it never saves.
//
//   · typed columns   — click a cell (the shared batch-edit cell editor)
//   · unit columns    — a ReagentUnits combo
//   · StockStdName    — read-only; DOUBLE-CLICK opens the "StockStdName"
//                       picker (ctrl :5263 → NPSampleFields_ListView_Reagent)
//   · right-click     — the shared Copy to All Cells / Copy to Selected Rows
//                       menu (XAF "CopyToAllCell" copies to the ticked rows)
//
// Formula and FinalConc are NOT computed here: the API recomputes them from
// WSconc / VolTaken / FinalVol on save (CalibrationLevelRules), so the numbers
// do not depend on this component having run (CLAUDE.md §1).
// ─────────────────────────────────────────────────────────────────────────────

/** Legacy picker messages (BTLIMS.Web Model.xafml LDMMessages). */
const MSG_NONE = 'Please select an item to proceed further.';
const MSG_MANY = 'Select only one check box.';

interface Props {
  prepId: string | null;
  prep: ReagentPrepDetail;
  /** The levels with the buffered edits already applied. */
  rows: ReagentPrepComponent[];
  units: ReagentLookupOption[];
  loading: boolean;
  /** Apply a patch to some levels' buffer. */
  onEdit: (oids: string[], patch: Partial<ReagentPrepComponent>) => void;
}

export function CalibrationLevelsGrid({ prepId, prep, rows, units, loading, onEdit }: Props) {
  const notify = useNotify();
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [menu, setMenu] = useState<{
    x: number; y: number; field: keyof ReagentPrepComponent; label: string; row: ReagentPrepComponent;
  } | null>(null);
  const [picker, setPicker] = useState<{ level: ReagentPrepComponent; options: string[] } | null>(null);
  const [pick, setPick] = useState<Set<string | number>>(new Set());

  const unitNames = useMemo(() => units.map((u) => u.name ?? '').filter(Boolean), [units]);

  /** The patch for setting one column to a value — a unit column carries its Id. */
  function patchFor(field: keyof ReagentPrepComponent, value: string | null, source?: ReagentPrepComponent) {
    const spec = LEVEL_EDIT[field];
    // N/A is the client-only placeholder: picking it CLEARS (never stored as text).
    const v = value === '' || value === NA_OPTION.label ? null : value;
    if (spec?.kind === 'unit') {
      const id = source ? (source[spec.idKey] as string | null | undefined) ?? null
                        : units.find((u) => u.name === v)?.id ?? null;
      return { [field]: v, [spec.idKey]: id } as Partial<ReagentPrepComponent>;
    }
    return { [field]: v } as Partial<ReagentPrepComponent>;
  }

  async function openPicker(level: ReagentPrepComponent) {
    if (!prepId) return;
    try {
      const options = await fetchStockStdOptions(prepId, {
        levelOid: level.oid,
        selectStockSolution: prep.selectStockSolution,
        vendorStockId: prep.vendorStockId,
        labStockId: prep.labStockId,
        levels: rows.map((r) => ({ oid: r.oid, componentID: Number(r.componentID ?? 0), workingStdName: r.workingStdName })),
      });
      setPick(new Set());
      setPicker({ level, options });
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to load the StockStdName list.');
    }
  }

  function acceptPick() {
    if (!picker) return;
    if (pick.size === 0) { notify.error(MSG_NONE); return; }
    if (pick.size > 1)   { notify.info(MSG_MANY); return; }
    onEdit([picker.level.oid], { stockStdName: String([...pick][0]) });
    setPicker(null);
  }

  function onContext(e: ReactMouseEvent<HTMLTableCellElement>, row: ReagentPrepComponent, field: keyof ReagentPrepComponent, label: string) {
    e.preventDefault();
    if (!canBulkCopy(row[field])) { notify.warning('Cell is empty.'); return; }
    setMenu({ x: e.clientX, y: e.clientY, field, label, row });
  }

  const columns: AppColumn<ReagentPrepComponent>[] = LEVEL_COLS.map((col) => {
    const field = col.field as keyof ReagentPrepComponent;
    const spec = LEVEL_EDIT[field];
    if (!spec) return col;
    const ctx = (e: ReactMouseEvent<HTMLTableCellElement>, row: ReagentPrepComponent) =>
      onContext(e, row, field, String(col.header));
    if (spec.kind === 'pick') {
      return {
        ...col,
        onCellContextMenu: ctx,
        render: (row: ReagentPrepComponent) => (
          <span className="rgp-pick-cell" title="Double-click to choose the StockStdName"
                onDoubleClick={() => void openPicker(row)}>
            {row.stockStdName ?? ''}
          </span>
        ),
      };
    }
    return {
      ...col,
      onCellContextMenu: ctx,
      edit: spec.kind === 'unit'
        ? { type: 'combo' as const, options: [NA_OPTION.label, ...unitNames], clearable: true }
        : { type: 'text' as const },
    };
  });

  return (
    <>
      <AppDataGrid<ReagentPrepComponent>
        rows={rows}
        columns={columns}
        getRowId={(r) => r.oid}
        loading={loading}
        emptyText=""
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        onCellCommit={(row, col, value) => {
          const field = col.field as keyof ReagentPrepComponent;
          onEdit([row.oid], patchFor(field, value));
        }}
      />

      {menu && (
        <CellContextMenu
          value={menu.row[menu.field]}
          columnLabel={menu.label}
          selectedCount={selected.size}
          position={{ x: menu.x, y: menu.y }}
          onClose={() => setMenu(null)}
          onCopyAll={() => {
            onEdit(rows.map((r) => r.oid), patchFor(menu.field, menu.row[menu.field] as string | null, menu.row));
            setMenu(null);
          }}
          onCopySelected={() => {
            if (selected.size === 0) { notify.warning('Select at least one row.'); setMenu(null); return; }
            onEdit([...selected].map(String), patchFor(menu.field, menu.row[menu.field] as string | null, menu.row));
            setMenu(null);
          }}
        />
      )}

      <AppDialog
        open={picker !== null}
        title="StockStdName"
        width={420}
        onClose={() => setPicker(null)}
        footer={
          <>
            <button type="button" className="ca-btn ca-btn-solid" onClick={acceptPick}>OK</button>
            <button type="button" className="ca-btn" onClick={() => setPicker(null)}>Cancel</button>
          </>
        }
      >
        <AppDataGrid<{ name: string }>
          rows={(picker?.options ?? []).map((name) => ({ name }))}
          columns={[{ field: 'name', header: 'Field Caption' }]}
          getRowId={(r) => r.name}
          selectable
          selectedIds={pick}
          onSelectionChange={setPick}
          emptyText="Choose the preparation's stock first."
        />
      </AppDialog>
    </>
  );
}
